import React, {
  useRef,
  useState,
  useContext,
  useCallback,
  createContext,
} from "react";
import Snackbar from "../../components/atoms/Snackbar";

interface ISnackbarContext {
  newInfo: (message: string) => void;
  newError: (message: string) => void;
  newSuccess: (message: string) => void;
  newWarning: (message: string) => void;
}

const defaultContext: ISnackbarContext = {
  newInfo: () => null,
  newError: () => null,
  newSuccess: () => null,
  newWarning: () => null,
};

const Context = createContext(defaultContext);
const useSnackbar = () => useContext(Context);

const Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [message, setMessage] = useState<string>("");
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [type, setType] = useState<"error" | "success" | "info" | "warning">(
    "info"
  );

  const timeout = useRef<NodeJS.Timeout | null>(null);

  const emptyTimeout = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
  }, []);

  const addTimeout = useCallback((callback: () => void) => {
    timeout.current = setTimeout(() => {
      callback();
    }, 3000);
  }, []);

  const clearAll = useCallback(() => {
    setMessage("");
    setType("info");
    setIsVisible(false);
    emptyTimeout();
  }, [emptyTimeout]);

  const setSnackbar = useCallback(
    (type: "error" | "info" | "success" | "warning", message: string) => {
      setType(type);
      setIsVisible(true);
      setMessage(message);

      emptyTimeout();
      addTimeout(clearAll);
    },
    [addTimeout, clearAll, emptyTimeout]
  );

  const newError = useCallback(
    (message: string) => {
      clearAll();
      setTimeout(() => {
        setSnackbar("error", message);
      }, 0);
    },
    [clearAll, setSnackbar]
  );

  const newSuccess = useCallback((message: string) => {
    clearAll();
    setTimeout(() => {
      setSnackbar("success", message);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newInfo = useCallback(
    (message: string) => {
      clearAll();
      setTimeout(() => {
        setSnackbar("info", message);
      }, 0);
    },
    [clearAll, setSnackbar]
  );

  const newWarning = useCallback(
    (message: string) => {
      clearAll();
      setTimeout(() => {
        setSnackbar("warning", message);
      }, 0);
    },
    [clearAll, setSnackbar]
  );

  const providerValue = {
    newInfo,
    newError,
    newSuccess,
    newWarning,
  };

  return (
    <Context.Provider value={providerValue}>
      {children}

      <Snackbar
        type={type}
        message={message}
        onClick={clearAll}
        isVisible={isVisible}
      />
    </Context.Provider>
  );
};

export default {
  useSnackbar,
  Provider,
};
